import { useMemo, useState } from "react";
import { boards, feedPosts, topList, hotUsers } from "@/mocks/community";
import Avatar from "./Avatar";

const orderTabs = [
  { key: "all", label: "综合" },
  { key: "new", label: "最新" },
  { key: "hot", label: "热议" },
];

export default function Board() {
  const [activeBoard, setActiveBoard] = useState("hot");
  const [activeOrder, setActiveOrder] = useState("all");
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const { list: matched, fallback } = useMemo(() => {
    let list = feedPosts.filter((p) => p.board === boards.find((b) => b.key === activeBoard)?.name);
    if (activeOrder === "new") list = list.slice().sort((a, b) => a.id - b.id);
    if (activeOrder === "hot") list = list.slice().sort((a, b) => b.heat - a.heat);
    if (!list.length) return { list: feedPosts.slice(0, 3), fallback: true };
    return { list, fallback: false };
  }, [activeBoard, activeOrder]);

  const filtered = matched;

  return (
    <section id="board" className="w-full bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 w-full">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider text-primary-600 uppercase">
              <span className="w-6 h-px bg-primary-500" aria-hidden="true"></span> 社区讨论区
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-black text-foreground-950">吃瓜广场 · 实时帖子流</h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
              按板块浏览最新帖子，参与评论、点赞与收藏，看看大家此刻都在聊什么。
            </p>
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 self-start">
            {orderTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveOrder(t.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeOrder === t.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* left nav */}
          <aside className="lg:col-span-1 rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-background-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-grid-fill text-primary-500" aria-hidden="true"></i> 全部板块
              </h3>
              <span className="text-xs text-foreground-400">8 个</span>
            </div>
            <ul className="p-2">
              {boards.map((b) => {
                const active = b.key === activeBoard;
                return (
                  <li key={b.key}>
                    <button
                      type="button"
                      onClick={() => setActiveBoard(b.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        active ? "bg-primary-100/80 text-primary-700" : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${b.color}`}>
                        <i className={b.icon} aria-hidden="true"></i>
                      </span>
                      <span className="font-medium flex-1 text-left whitespace-nowrap">{b.name}</span>
                      <span className={`text-xs ${active ? "text-primary-500" : "text-foreground-400"}`}>{b.posts}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <a
              href="#join"
              className="mx-4 mb-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent-100 text-accent-700 text-sm font-semibold hover:bg-accent-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line" aria-hidden="true"></i> 开通我的板块
            </a>
          </aside>

          {/* feed */}
          <div className="lg:col-span-2 space-y-3">
            {fallback && (
              <p className="text-xs text-foreground-500 bg-accent-100/70 text-accent-800 rounded-xl px-4 py-2.5 flex items-center gap-2">
                <i className="ri-information-line" aria-hidden="true"></i>
                该板块暂无最新置顶，为你推荐广场热帖。
              </p>
            )}
            {filtered.map((p) => (
              <article
                key={p.id}
                className="group rounded-2xl bg-background-50 border border-background-200 hover:border-primary-200 p-4 md:p-5 transition-colors"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {p.pinned && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-bold">
                      <i className="ri-pushpin-fill text-[11px]" aria-hidden="true"></i> 置顶
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[11px]">{p.board}</span>
                  {p.topics.map((t) => (
                    <span key={t} className="text-[11px] text-accent-700 hover:underline cursor-pointer">
                      {t}
                    </span>
                  ))}
                </div>
                <a href="#join" className="block mt-2.5">
                  <h4 className="text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                    {p.title}
                  </h4>
                </a>
                <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{p.desc}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar char={p.avatarChar} tone={p.tone} size="w-7 h-7 text-xs" />
                    <span className="text-xs text-foreground-600 truncate whitespace-nowrap">{p.author}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">· {p.time}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {(p.views / 1000).toFixed(1)}k
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-chat-3-line" aria-hidden="true"></i>
                      {p.replies}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLiked((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors ${
                        liked[p.id] ? "text-primary-600" : "hover:text-primary-600"
                      }`}
                      aria-pressed={!!liked[p.id]}
                    >
                      <i className={liked[p.id] ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
                      {p.likes + (liked[p.id] ? 1 : 0)}
                    </button>
                  </div>
                </div>
              </article>
            ))}

            <a
              href="#board"
              className="flex items-center justify-center gap-2 mt-1 w-full py-3 rounded-2xl bg-background-50 border border-dashed border-foreground-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 text-sm font-medium transition-colors"
            >
              查看更多帖子 <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
            </a>
          </div>

          {/* right sidebar */}
          <aside className="lg:col-span-1 space-y-5">
            <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-background-200 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true"></i> 今日热榜
                </h3>
                <span className="text-xs text-foreground-400">每 5 分钟更新</span>
              </div>
              <ol className="p-3">
                {topList.map((t, i) => (
                  <li key={t.rank}>
                    <a
                      href="#feature"
                      className="flex items-start gap-3 px-2 py-2 rounded-xl hover:bg-background-100 transition-colors"
                    >
                      <span
                        className={`w-5 h-5 rounded-md flex items-center justify-center text-xs font-black shrink-0 ${
                          i < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500"
                        }`}
                      >
                        {t.rank}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 line-clamp-2 leading-snug">{t.title}</p>
                        <p className="mt-1 text-[11px] text-primary-500 font-medium">{t.heat}</p>
                      </div>
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-background-200 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-user-heart-line text-primary-500" aria-hidden="true"></i> 活跃瓜友
                </h3>
                <i className="ri-verified-badge-fill text-accent-500 text-sm" aria-hidden="true"></i>
              </div>
              <ul className="p-2">
                {hotUsers.map((u) => (
                  <li key={u.name} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-background-100 transition-colors">
                    <Avatar char={u.char} tone={u.tone} size="w-9 h-9 text-sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground-900 flex items-center gap-1">
                        {u.name}
                        <i className="ri-verified-badge-fill text-accent-500 text-xs" aria-hidden="true"></i>
                      </p>
                      <p className="text-[11px] text-foreground-400">{u.intro}</p>
                    </div>
                    <a
                      href="#join"
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      关注
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}